import { FunctionComponent, useState } from "react";

import {
  DefaultButton,
  Dialog,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  SubmissionForm,
  UseFormMethods,
  useTheme
} from "@bps/fluent-ui";
import { HttpError } from "@bps/http-client";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  DeviceDetail,
  DeviceSearchSelectorCreateType
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSelectorDataTypeRefData } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SelectorFormValues } from "../selectors/edit/formUtils";
import {
  selectorFormSchema,
  selectorFormValidator
} from "../selectors/edit/SelectorFormValidator";
import {
  operatorOptions,
  SelectorRuleTable
} from "../selectors/edit/SelectorRuleTable";
import { CreateSelectorFromCriteriaButton } from "./CreateSelectorFromCriteriaButton";
import { CreateSelectorModal } from "./CreateSelectorModal";

interface DeviceSearchFormProps {
  onSubmit: (
    values: SelectorFormValues,
    actions: Pick<UseFormMethods<SelectorFormValues>, "reset" | "setValue">
  ) => Promise<void>;
  isLoading: boolean;
  error?: HttpError | null;
  selectedDevices: DeviceDetail[];
}

export type SelectorCreateType =
  | DeviceSearchSelectorCreateType.FromCriteria
  | DeviceSearchSelectorCreateType.FromDevices;

export const DeviceSearchFormBase: React.FC<DeviceSearchFormProps> = ({
  onSubmit,
  isLoading,
  error,
  selectedDevices
}) => {
  const theme = useTheme();

  const [createType, setCreateType] = useState<SelectorCreateType>(
    DeviceSearchSelectorCreateType.FromCriteria
  );

  const [showLikeDisallowed, setShowLikeDisallowed] = useState(false);
  const [showCreateSelector, setShowCreateSelector] = useState(false);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const defaultValues: SelectorFormValues = {
    name: "",
    description: "",
    rules: [
      {
        attributeType: "",
        value: "",
        comparisonOperator: operatorOptions[0].key, // default operator is "="
        id: crypto.randomUUID(),
        valueDataType: "STRING"
      }
    ]
  };

  return (
    <SubmissionForm<SelectorFormValues>
      onSubmit={onSubmit}
      hideButtons={true}
      initialValues={defaultValues}
      disabled={!hasWritePermission}
      validate={values =>
        selectorFormValidator.validate(values, selectorFormSchema)
      }
      styles={{
        root: {
          width: "100%"
        },
        buttons: {
          margin: 0
        }
      }}
    >
      {(form, actions) => {
        return (
          <>
            {error && (
              <MessageBar messageBarType={MessageBarType.error}>
                {error?.detail as string}
              </MessageBar>
            )}

            <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
              <SelectorRuleTable />
            </Stack>
            <Stack
              horizontal
              tokens={{ childrenGap: theme.spacing.s1 }}
              horizontalAlign="end"
            >
              <DefaultButton
                onClick={() => {
                  setCreateType(DeviceSearchSelectorCreateType.FromDevices);
                  setShowCreateSelector(true);
                  setShowLikeDisallowed(false);
                }}
                disabled={
                  form.invalid ||
                  isLoading ||
                  !selectedDevices ||
                  selectedDevices.length === 0
                }
              >
                Create Selector from Selected Devices
              </DefaultButton>
              <CreateSelectorFromCriteriaButton
                onCreate={isCreateAllowed => {
                  if (isCreateAllowed) {
                    setCreateType(DeviceSearchSelectorCreateType.FromCriteria);
                    setShowCreateSelector(true);
                    setShowLikeDisallowed(false);
                  } else {
                    setShowLikeDisallowed(true);
                  }
                }}
                disabled={
                  form.invalid || isLoading || form.values.rules?.length === 0
                }
              >
                Create Selector from Criteria
              </CreateSelectorFromCriteriaButton>
              <PrimaryButton
                onClick={() => onSubmit(form.values, actions)}
                disabled={form.invalid || isLoading}
              >
                Search
              </PrimaryButton>
            </Stack>

            {showCreateSelector && (
              <CreateSelectorModal
                show={showCreateSelector}
                onDismiss={() => setShowCreateSelector(false)}
                selector={form.values!}
                selectedDevices={selectedDevices ?? []}
                createType={createType}
              />
            )}

            {showLikeDisallowed && (
              <Dialog
                hidden={false}
                dialogContentProps={{
                  subText:
                    "Unable to create a selector with a LIKE comparison operator.",
                  title: "Cannot create selector"
                }}
                maxWidth={540}
                minWidth={540}
              >
                <Stack horizontalAlign="end">
                  <DefaultButton
                    onClick={() => {
                      setShowLikeDisallowed(false);
                    }}
                    styles={{ root: { width: "fit-content" } }}
                  >
                    Close
                  </DefaultButton>
                </Stack>
              </Dialog>
            )}
          </>
        );
      }}
    </SubmissionForm>
  );
};

export const DeviceSearchForm: FunctionComponent<DeviceSearchFormProps> = props => {
  const dataTypesQuery = useSelectorDataTypeRefData();

  return (
    <QueryStateIndicator {...dataTypesQuery}>
      <DeviceSearchFormBase {...props} />
    </QueryStateIndicator>
  );
};
